/**
 * UTC 시간을 로컬 시간으로 변환하여 포맷팅
 * 
 * @param utcDateString - UTC 시간 문자열
 * @param options - 포맷 옵션 (기본값: 날짜+시간)
 * @returns 로컬 시간 문자열
 */
export const formatLocalDateTime = (
    utcDateString: string | null | undefined,
    options?: {
        dateOnly?: boolean;  // true면 날짜만 표시
        timeOnly?: boolean;  // true면 시간만 표시
    }
): string => {
    if (!utcDateString) return "-";

    try {
        let dateString = utcDateString.trim();

        // 1. 공백을 T로 치환 (ISO 8601 호환성, 일부 브라우저/포맷 대응)
        dateString = dateString.replace(' ', 'T');

        // 1.1 Remove 'Z' if it follows a timezone offset (e.g., +09:00Z -> +09:00)
        // Backend might be sending both, satisfying neither parser fully if strict.
        if (/([+-]\d{2}:?\d{2})Z$/.test(dateString)) {
            dateString = dateString.replace(/Z$/, '');
        }

        // Check if only date is provided (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            dateString += 'T00:00:00';
        }

        // 2. 시간대 오프셋이 없으면 KST(+09:00)로 간주
        // (백엔드가 KST 시간을 timezone 없이 보내는 것으로 확인됨)
        const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/.test(dateString);
        if (!hasTimezone) {
            dateString += '+09:00';
        }

        const utcDate = new Date(dateString);

        // 날짜 유효성 검사
        if (isNaN(utcDate.getTime())) {
            console.error('Invalid Date string:', utcDateString);
            return "-";
        }

        const kstOptions: Intl.DateTimeFormatOptions = {
            timeZone: 'Asia/Seoul',
            hour12: false,
        };

        // 날짜만 표시
        if (options?.dateOnly) {
            return utcDate.toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                ...kstOptions
            });
        }

        // 시간만 표시
        if (options?.timeOnly) {
            return utcDate.toLocaleTimeString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                ...kstOptions
            });
        }

        // 날짜 + 시간 표시 (기본값)
        return utcDate.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            ...kstOptions
        });
    } catch (error) {
        console.error('날짜 변환 오류:', error);
        return "-";
    }
};

/**
 * UTC 날짜를 로컬 날짜로 변환 (날짜만)
 */
export const formatLocalDate = (utcDateString: string | null | undefined): string => {
    return formatLocalDateTime(utcDateString, { dateOnly: true });
};

/**
 * UTC 시간을 로컬 시간으로 변환 (시간만)
 */
export const formatLocalTime = (utcDateString: string | null | undefined): string => {
    return formatLocalDateTime(utcDateString, { timeOnly: true });
};
